/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ProntoLogo from "@/components/ProntoLogo";
import PricingSimulator from "@/components/PricingSimulator";
import useAppInfo from "@/lib/useAppInfo";
import { Check, ArrowRight, ArrowLeft, Sparkle, ShieldCheck, Heart } from "@phosphor-icons/react";
import { isReaderMode } from "@/lib/iosReader";

const SUPPORT_EMAIL = "supporto@prontoimmo.it";

const PLANS = [
  {
    id: "free",
    name: "Free",
    tag: "Per provare Pronto senza carta.",
    price: { monthly: 0, annual: 0 },
    note: "Gratis per sempre · senza carta di credito",
    // iter v33 r106 · Promo lancio: 60 crediti UNA TANTUM.
    // Post-promo: 10 crediti/mese standard (era 5, bump business decision r106).
    features: [
      "1 immobile attivo",
      "60 crediti gratis inclusi per i nuovi iscritti",
      "10 crediti/mese ricorrenti (i crediti promo residui restano disponibili)",
      "Pagina personale con logo Pronto",
      "Post social NON inclusi",
      "Logo Pronto sempre visibile sui contenuti",
    ],
    ctaLabel: "Iscriviti · 60 crediti gratis",
    highlight: false,
  },
  {
    id: "base",
    name: "Start",
    tag: "Per partire bene, 1-2 immobili al mese.",
    price: { monthly: 29, annual: 249 },
    note: "Annuale: 3 mesi in regalo · risparmi €99",
    features: [
      "5 immobili attivi",
      "150 crediti smart al mese (es. 30 staging · o 30 reel · o 150 foto)",
      "Post · reel · story · carosello",
      "Selezione foto migliori per i social",
      "Sopralluogo vocale · Assistente Pronto",
      "Estensione Chrome per Idealista e Immobiliare.it",
      "Logo e colori della tua agenzia",
      "Logo Pronto resta sui contenuti",
    ],
    ctaLabel: "Provi Pronto gratis",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    tag: "Tutto illimitato, niente watermark.",
    price: { monthly: 49, annual: 399 },
    note: "Annuale: 3 mesi in regalo · risparmi €189",
    features: [
      "Immobili senza limite di numero",
      "320 crediti smart al mese (es. 64 staging · o 64 reel · o 320 foto)",
      "Niente watermark Pronto sui contenuti",
      "Assistente Pronto illimitato gratuito (fuori crediti)",
      "Pianta arredata in HD",
      "Tutto il piano Start",
      "Supporto prioritario via email",
    ],
    ctaLabel: "Provi Pronto gratis",
    highlight: true,
    badge: "Più scelto",
  },
  // iter v33 r99g · User M1500: piano "Agenzia" (pro_agency) rimosso dalle
  // comunicazioni pubbliche fino al lancio dedicato. Backend/PLAN_BUCKETS
  // mantiene la definizione ma non viene esposto al pubblico.
];

const COMPARISON = [
  {
    section: "Prezzo",
    rows: [
      { label: "Prezzo mensile",          values: ["€0", "€29 / mese", "€49 / mese"] },
      { label: "Prezzo annuale (sconto)", values: ["€0", "€249 / anno", "€399 / anno"] },
      { label: "Carta di credito richiesta", values: ["No", "Sì (al primo pagamento)", "Sì (al primo pagamento)"] },
      { label: "Disdici quando vuoi (nessun vincolo)", values: ["—", "✓", "✓"] },
      { label: "Fattura elettronica via SDI inclusa", values: ["✓", "✓", "✓"] },
    ],
  },
  {
    section: "Crediti smart al mese · stesso bucket per ogni servizio",
    rows: [
      { label: "Crediti inclusi al mese",      values: ["60* / poi 10", "150", "320"] },
      { label: "Foto migliorata", values: ["1 cr", "1 cr", "1 cr"] },
      { label: "Arreda spazio vuoto · Rinnova arredo", values: ["—", "5 cr", "5 cr"] },
      { label: "Svuota stanza arredata", values: ["—", "3 cr", "3 cr"] },
      { label: "Pianta arredata (vista dall'alto)", values: ["—", "3 cr", "3 cr"] },
      { label: "Sopralluogo vocale (parli, scrive Pronto)", values: ["—", "1 cr", "1 cr"] },
      { label: "Audio check sopralluogo completo", values: ["—", "5 cr", "5 cr"] },
      { label: "Domanda all'Assistente Pronto", values: ["—", "1 cr", "incluso gratuito"] },
      { label: "Reel Prima/Dopo (video 5s, HD)", values: ["—", "5 cr", "5 cr"] },
      { label: "Carosello social · Post singolo", values: ["—", "1 cr", "1 cr"] },
      { label: "Pack social completo (ZIP)", values: ["—", "3 cr", "3 cr"] },
      { label: "Valutazione immobile", values: ["—", "1 cr", "1 cr"] },
      { label: "Caption testi automatica", values: ["—", "0 cr (gratuito)", "0 cr (gratuito)"] },
      { label: "Pacchetti extra (top-up)", values: ["—", "da €5 / 20 cr", "da €5 / 20 cr"] },
      { label: "I crediti aggiuntivi non scadono finché il piano è attivo", values: ["—", "✓", "✓"] },
    ],
  },
  {
    section: "Gestione immobili",
    rows: [
      { label: "Immobili attivi",            values: ["1", "5", "Senza limite"] },
      { label: "Pianta arredata in HD", values: ["—", "—", "✓"] },
    ],
  },
  {
    section: "Editor social",
    rows: [
      { label: "Post Instagram · 1:1 / 4:5 / 9:16", values: ["—", "✓", "✓"] },
      { label: "Reel cinematografico (Ken Burns, 9:16 / 1:1)", values: ["—", "✓ (5 cr / reel)", "✓ (5 cr / reel)"] },
      { label: "Reel Prima/Dopo (virtual staging · restyle)", values: ["—", "✓ (5 cr)", "✓ (5 cr)"] },
      { label: "Carosello Instagram 5 slide (ZIP pronto)", values: ["—", "✓ (1 cr)", "✓ (1 cr)"] },
      { label: "Story 9:16 · Open House · Venduto", values: ["—", "✓", "✓"] },
      { label: "Selezione automatica delle foto migliori per i social", values: ["—", "✓", "✓"] },
      { label: "Scelta manuale della foto per Post / Reel", values: ["—", "✓", "✓"] },
      { label: "Didascalia automatica (testo, gratuitamente)", values: ["—", "✓", "✓"] },
      { label: "Watermark Pronto sui contenuti", values: ["sempre", "sempre", "—"] },
    ],
  },
  {
    section: "Pubblicazione · portali",
    rows: [
      { label: "Estensione Chrome per Idealista e Immobiliare.it", values: ["—", "✓", "✓"] },
      { label: "Esportazione XML personale", values: ["—", "✓", "✓"] },
      { label: "Esportazione XML agenzia (1 URL multi-agente)", values: ["—", "—", "—"] },
    ],
  },
  {
    section: "Agenda · contatti · CRM",
    rows: [
      { label: "Agenda manuale (memo testuali)",            values: ["✓", "✓", "✓"] },
      { label: "Sopralluogo vocale (parli, scrive Pronto)", values: ["—", "✓ (1 cr)", "✓ (1 cr)"] },
      { label: "Agenda vocale (memo + appuntamenti)", values: ["—", "✓", "✓"] },
      { label: "Punteggio contatti automatico",              values: ["✓", "✓", "✓"] },
    ],
  },
  {
    section: "Assistente Pronto · consulente immobiliare",
    rows: [
      { label: "Domande all'Assistente Pronto", values: ["—", "✓ (1 cr / domanda)", "✓ illimitato gratuito"] },
      { label: "Materia immobiliare (contratti, fisco, codice civile)", values: ["—", "✓", "✓"] },
      { label: "Aiuto sull'uso di Pronto", values: ["—", "✓", "✓"] },
    ],
  },
  {
    section: "Acquisizione clienti",
    rows: [
      { label: "Pagina personale brandizzata",            values: ["Base", "✓", "✓"] },
      { label: "Landing /quanto-vale-casa-mia con QR",       values: ["—", "✓", "✓"] },
      { label: "Importa da gestionale (XML)",       values: ["—", "✓", "✓"] },
    ],
  },
  {
    section: "Brand · supporto",
    rows: [
      { label: "Brand tuo (logo, colore, email)",  values: ["—", "✓", "✓"] },
      { label: "Logo Pronto + prontoimmo.it sui contenuti",  values: ["sempre", "sempre", "—"] },
      { label: "Supporto prioritario via email",         values: ["—", "—", "✓"] },
      { label: "Onboarding dedicato",                     values: ["—", "—", "—"] },
    ],
  },
];

function PlanCard({ plan, billing, onChoose, currentPlan, loading, betaMode }) {
  const isCurrent = currentPlan === plan.id;
  const price = plan.price[billing];
  const isFree = plan.id === "free";
  const dark = plan.highlight;
  const isComingSoon = !!plan.comingSoon;
  // In beta:
  //  - Free/Base/Pro → "Incluso in beta"
  //  - Agency → "Contattaci" (mailto, mai pagamento online)
  const isTeamOrAgency = plan.id === "pro_team" || plan.id === "pro_agency";
  const ctaLabel = isComingSoon
    ? "Contattaci"
    : betaMode
      ? (isTeamOrAgency ? "Disponibile al lancio" : (isFree ? "Iscrizione gratuita" : "Incluso gratuitamente"))
      : plan.ctaLabel;
  return (
    <div
      className="relative card-flat p-8 flex flex-col"
      style={{
        background: dark ? "#7C8B6E" : "#FFFFFF",
        borderColor: dark ? "#7C8B6E" : "#E5E7E0",
        color: dark ? "#FFFFFF" : undefined,
      }}
      data-testid={`plan-${plan.id}`}
    >
      {plan.badge && !(betaMode && isTeamOrAgency) && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.2em] uppercase px-3 py-1 bg-[#7C8B6E] text-white">
          {plan.badge}
        </span>
      )}
      {betaMode && isTeamOrAgency && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.2em] uppercase px-3 py-1 bg-[#1C1C1A] text-white">
          In arrivo
        </span>
      )}
      <span
        className="text-[0.72rem] uppercase font-semibold"
        style={{ letterSpacing: "0.25em", color: dark ? "#7C8B6E" : "#7C8B6E" }}
      >
        {plan.name}
      </span>
      <p className="mt-2 text-sm" style={{ color: dark ? "rgba(251,251,249,0.85)" : "#6B6A65" }}>
        {plan.tag}
      </p>
      <div className="mt-5 flex items-baseline gap-2">
        {isComingSoon && (price === null || price === undefined) ? (
          <span className="font-editorial text-4xl tracking-tight" style={{ color: dark ? "#FFFFFF" : "#1C1C1A" }}>
            Su misura
          </span>
        ) : betaMode && !isFree ? (
          <>
            <span className="font-editorial text-5xl tracking-tight" style={{ color: dark ? "#FFFFFF" : "#1C1C1A" }}>
              €{price}
            </span>
          </>
        ) : (
          <span className="font-editorial text-5xl tracking-tight" style={{ color: dark ? "#FFFFFF" : "#1C1C1A" }}>
            {price === 0 ? "€0" : `€${price}`}
          </span>
        )}
        {!(isComingSoon && (price === null || price === undefined)) && (
          <span style={{ color: dark ? "rgba(251,251,249,0.75)" : "#6B6A65" }}>
            {isComingSoon ? "/ mese · al lancio" : betaMode && !isFree ? "/ mese · al lancio" : (isFree ? "/ sempre" : billing === "monthly" ? "/ mese" : "/ anno")}
          </span>
        )}
      </div>
      {/* iter v30 r050 — Regime forfettario art. 1 c. 54-89 L. 190/2014.
          Il titolare opera in regime forfettario: opera senza applicazione
          dell'IVA, quindi il prezzo mostrato corrisponde all'importo finale
          addebitato. Non c'è "+ IVA" né "IVA inclusa". */}
      {!isFree && price !== null && price !== undefined && (
        <div className="mt-1 text-[11px] font-medium tracking-wide" style={{ color: dark ? "rgba(251,251,249,0.85)" : "#7C8B6E" }}>
          Importo finale · senza applicazione IVA
        </div>
      )}
      <p className="mt-1 text-xs" style={{ color: dark ? "rgba(251,251,249,0.6)" : "#6B6A65" }}>
        {plan.note}
      </p>

      {plan.trialBadge && billing === "monthly" && (
        <div
          className="mt-4 text-xs px-3 py-2 tracking-wider uppercase font-medium text-center"
          style={{
            background: dark ? "rgba(192,90,65,0.2)" : "rgba(192,90,65,0.1)",
            color: dark ? "#FFB8A1" : "#7C8B6E",
          }}
        >
          {plan.trialBadge}
        </div>
      )}

      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-[14px] leading-relaxed">
            <Check
              size={16}
              weight="bold"
              className="flex-shrink-0 mt-0.5"
              style={{ color: dark ? "#7C8B6E" : "#7C8B6E" }}
            />
            <span style={{ color: dark ? "#FFFFFF" : "#1C1C1A" }}>{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => {
          if (isComingSoon) {
            window.location.href = "/contatti?argomento=enterprise";
          } else {
            onChoose(plan);
          }
        }}
        disabled={loading || (isCurrent && !isComingSoon)}
        className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm font-medium transition-colors disabled:opacity-60 ${
          dark
            ? "bg-[#7C8B6E] text-white hover:bg-[#a34a35]"
            : isFree
            ? "border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-[#FFFFFF]"
            : isComingSoon
            ? "border border-[#1C1C1A] text-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-[#FFFFFF]"
            : "bg-[#7C8B6E] text-[#FFFFFF] hover:bg-[#163029]"
        }`}
        data-testid={`plan-${plan.id}-btn`}
      >
        {isCurrent ? "Piano attuale" : loading ? "Caricamento..." : (
          <>{ctaLabel} <ArrowRight size={14} weight="bold" /></>
        )}
      </button>

      {!isFree && !isComingSoon && !isCurrent && (
        <p
          className="mt-3 text-xs text-center leading-relaxed"
          style={{ color: dark ? "rgba(251,251,249,0.85)" : "#7C8B6E" }}
          data-testid={`plan-${plan.id}-promo-subtext`}
        >
          60 crediti gratis · nessuna carta richiesta
        </p>
      )}

      {dark && billing === "monthly" && (
        <p className="mt-3 text-xs text-center" style={{ color: "rgba(251,251,249,0.7)" }}>
          Disdici quando vuoi · nessun vincolo
        </p>
      )}
    </div>
  );
}


export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const appInfo = useAppInfo();
  const betaMode = appInfo?.beta_mode !== false;
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);
  // iter v29 r275 — Modal recesso 14gg (Codice del Consumo art. 59 lett. o)
  // Apre quando l'utente clicca un piano a pagamento. Senza checkbox spuntata,
  // il checkout non può partire (lato server è anche bloccato).
  const [waiverDialog, setWaiverDialog] = useState({ open: false, plan: null });
  const [waiverChecked, setWaiverChecked] = useState(false);

  // iter v30 r137 — Apple Reader-app compliance: la pagina /pricing NON
  // deve essere visibile dentro l'app iOS native. Su iOS reindirizziamo
  // l'utente alla dashboard. La pagina resta accessibile da web.
  useEffect(() => {
    if (isReaderMode) {
      nav(user ? "/dashboard" : "/login", { replace: true });
    }
  }, [nav, user]);

  const startCheckout = async (plan, withdrawalWaiver) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: plan.id === "base" ? "base" : "pro",
        billing_period: billing,
        origin_url: window.location.origin,
        withdrawal_waiver: withdrawalWaiver,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio pagamento");
      setLoading(false);
    }
  };

  const onChoose = async (plan) => {
    if (betaMode && plan.id !== "free") {
      // In beta i piani a pagamento non sono attivi: portiamo l'utente in dashboard.
      // Il pulsante mostra già "Disponibile al lancio".
      nav(user ? "/dashboard" : "/register?next=/dashboard");
      return;
    }
    if (plan.id === "free") {
      nav(user ? "/dashboard" : "/register?next=/dashboard");
      return;
    }
    if (!user) {
      nav("/register?next=/pricing");
      return;
    }
    // Apre il dialog di consenso recesso prima del checkout
    setWaiverChecked(false);
    setWaiverDialog({ open: true, plan });
  };

  const confirmWaiverAndCheckout = () => {
    if (!waiverChecked || !waiverDialog.plan) return;
    const plan = waiverDialog.plan;
    setWaiverDialog({ open: false, plan: null });
    startCheckout(plan, true);
  };

  // iter v30 r137 — Reader-mode: blocca render della pagina su iOS native
  // (il redirect avviene già nell'useEffect sopra).
  if (isReaderMode) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link-pricing">
            <img src="/logo/full-with-tagline.png?v=2" alt="Pronto · per agenti immobiliari" className="h-12 sm:h-14 w-auto select-none" draggable="false" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#6B6A65] hover:text-[#7C8B6E] flex items-center gap-1.5">
              <ArrowLeft size={14} /> Home
            </Link>
            <Link to={user ? "/dashboard" : "/login"} className="text-sm hover:text-[#7C8B6E]" data-testid="header-account-link">
              {user ? "Dashboard" : "Accedi"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {/* Title */}
        <div className="text-center max-w-2xl mx-auto">
          {betaMode && (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] text-amber-800 font-medium px-3 py-1.5 bg-amber-100 mb-5" data-testid="pricing-beta-pill">
              <Sparkle size={11} weight="fill" /> Gratis
            </span>
          )}
          <h1 className="font-editorial text-5xl lg:text-6xl tracking-tight" data-testid="pricing-title">
            {betaMode ? (
              <>
                I piani<br/>
                <span className="italic-accent text-[#7C8B6E]">al lancio.</span>
              </>
            ) : (
              <>
                Scegli il tuo<br/>
                <span className="italic-accent text-[#7C8B6E]">Piano.</span>
              </>
            )}
          </h1>
          <p className="mt-6 text-lg text-[#6B6A65]">
            {betaMode
              ? "Provi tutte le funzioni di Pronto con quote pensate per usarlo davvero. Scegli il piano che ti serve quando vuoi."
              : "Scegli il piano in base a quanti crediti ti servono al mese. Disdici quando vuoi, senza vincoli."}
          </p>
        </div>

        {betaMode && (
          <div className="mt-10 max-w-3xl mx-auto p-5 lg:p-6 rounded-lg bg-gradient-to-br from-amber-50 to-white border border-amber-200" data-testid="pricing-beta-banner">
            <div className="flex items-start gap-3">
              <Sparkle size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-editorial text-xl text-[#1C1C1A]">Prova Pronto gratis.</h2>
                <p className="mt-1.5 text-[14px] text-[#1C1C1A] leading-relaxed">
                  Ogni nuovo iscritto riceve <strong>60 crediti gratis una tantum</strong> per provare tutte le funzioni: foto migliorate, sopralluogo vocale, virtual staging, social, portali. Nessuna carta richiesta. Il piano Free continua con <strong>10 crediti/mese</strong> — i crediti promo residui restano nel tuo account come credito bonus e non scadono.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Billing toggle */}
        <div className="mt-12 flex justify-center">
          <div className="inline-flex border border-[#E5E7E0] bg-white p-1 rounded-sm" role="tablist" data-testid="billing-toggle">
            {[
              { id: "monthly", l: "Mensile" },
              { id: "annual", l: "Annuale" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setBilling(opt.id)}
                className={`px-5 py-2 text-sm font-medium transition-colors ${
                  billing === opt.id ? "bg-[#7C8B6E] text-[#FFFFFF]" : "text-[#1C1C1A] hover:text-[#7C8B6E]"
                }`}
                data-testid={`billing-${opt.id}`}
              >
                {opt.l}
                {opt.id === "annual" && (
                  <span className={`ml-2 text-[10px] tracking-widest uppercase ${
                    billing === "annual" ? "text-[#7C8B6E]" : "text-[#7C8B6E]"
                  }`}>
                    fino a -32%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-3">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billing={billing}
              onChoose={onChoose}
              currentPlan={user?.plan}
              loading={loading}
              betaMode={betaMode}
            />
          ))}
        </div>

        {/* iter v29 r215 — Simulatore "Quanto pago?" subito sotto le card.
            Trasforma la scelta in trasparente: l'agente vede il piano
            consigliato in base agli immobili che gestisce davvero. */}
        <PricingSimulator />

        {/* Enterprise card */}
        <div className="mt-6 card-flat p-8 bg-white" data-testid="plan-enterprise">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex-1">
              <span className="overline">Su misura</span>
              <h3 className="font-editorial text-3xl mt-2">Enterprise</h3>
              <p className="mt-3 text-[#6B6A65] max-w-2xl">
                Hai più sedi o sei un franchising? Pronto ha un piano <strong>Enterprise</strong> pensato per te:
              </p>
              <ul className="mt-4 grid grid-cols-1 gap-y-1.5 text-[13px] text-[#1C1C1A]">
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Crediti foto personalizzati sul tuo volume</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Editor immagini completo</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Gestionale immobili centralizzato</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Agenti e sedi multiple dallo stesso pannello</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Assistente automatico per le descrizioni</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5" /> Supporto dedicato</li>
              </ul>
            </div>
            <Link
              to="/contatti?argomento=enterprise"
              className="btn-outline self-start whitespace-nowrap"
              data-testid="enterprise-contact-btn"
            >
              Contattaci <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>

        {/* iter 313 r23 — WhatsAppCalculator rimosso dal pricing.
            Confondeva: gli agenti pensano "WhatsApp ce l'ho già" perché il
            bottone "Condividi su WhatsApp" (wa.me) è già attivo in Pronto.
            La feature vera (Business API + invio automatico CRM) non è
            ancora pronta. Rimettere quando il flusso è davvero integrato. */}

        {/* iter v28 r286 — USP "il tuo account, non dell'agenzia".
            Posizionata strategicamente tra la card Enterprise e la tabella di
            confronto: l'agente che valuta Pronto la vede mentre sta già
            confrontando i piani. Pitch differenziante vs Gestim/Mediasi. */}
        <div className="mt-12 card-flat overflow-hidden bg-gradient-to-br from-[#F1F4ED] via-white to-[#FAF8F3] border-2 border-[#1C1C1A]" data-testid="usp-account-portable">
          <div className="p-7 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-[#1C1C1A] text-white flex items-center justify-center">
                <ShieldCheck size={24} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
                  Il tuo account · non quello dell'agenzia
                </div>
                <h2 className="font-editorial text-3xl sm:text-4xl text-[#1C1C1A] mt-2 leading-tight tracking-tight">
                  Se cambi azienda, non perdi nulla.
                </h2>
                <p className="text-[14px] text-[#3A3A36] leading-relaxed mt-4 max-w-2xl">
                  Pronto è il software dell'<strong>agente</strong>, non dell'agenzia. Recensioni,
                  immobili caricati da te, lead, contatti, sopralluoghi, post — sono collegati al
                  tuo profilo personale. Cambi RE/MAX, Tecnocasa, Gabetti, ti metti in proprio?
                  Apri Pronto, fai login, è tutto lì.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white border border-[#E5E2D8] rounded-lg p-3.5">
                    <div className="font-semibold text-[13px] text-[#1C1C1A]">Recensioni tue</div>
                    <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
                      Costruite con i tuoi clienti, restano sul tuo profilo per sempre.
                    </p>
                  </div>
                  <div className="bg-white border border-[#E5E2D8] rounded-lg p-3.5">
                    <div className="font-semibold text-[13px] text-[#1C1C1A]">Immobili tuoi</div>
                    <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
                      Gli annunci caricati da te seguono il tuo account, non l'agenzia.
                    </p>
                  </div>
                  <div className="bg-white border border-[#E5E2D8] rounded-lg p-3.5">
                    <div className="font-semibold text-[13px] text-[#1C1C1A]">Lead tuoi</div>
                    <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
                      I contatti generati dalla tua pagina personale restano nella tua rubrica.
                    </p>
                  </div>
                </div>

                <p className="text-[12px] text-[#6B6A65] mt-5 inline-flex items-center gap-1.5">
                  <Heart size={13} weight="fill" className="text-[#B58A4E]" />
                  <span>Un nostro impegno scritto: quello che costruisci qui dentro è tuo, ovunque tu vada.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mt-24">
          <h2 className="font-editorial text-3xl lg:text-4xl text-center tracking-tight">
            Confronta i <span className="italic-accent text-[#7C8B6E]">piani</span>
          </h2>
          <div className="mt-10 card-flat overflow-x-auto bg-white">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-[#E5E7E0]">
                  <th className="text-left p-5 overline">Funzionalità</th>
                  <th className="text-center p-5 font-editorial text-base">Free</th>
                  <th className="text-center p-5 font-editorial text-base">Start</th>
                  <th className="text-center p-5 font-editorial text-base">Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((sec) => (
                  <React.Fragment key={sec.section}>
                    <tr className="bg-[#F1F4ED]">
                      <td colSpan={4} className="p-3 px-5 overline">{sec.section}</td>
                    </tr>
                    {sec.rows.map((row, i) => (
                      <tr key={i} className="border-b border-[#E5E7E0]">
                        <td className="p-4 px-5 text-[#1C1C1A]">{row.label}</td>
                        {row.values.map((v, j) => (
                          <td key={j} className={`p-4 text-center ${v === "✓" ? "text-[#7C8B6E] font-bold" : v === "—" ? "text-[#A8A6A0]" : "text-[#1C1C1A]"}`}>
                            {v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-center text-sm text-[#6B6A65]">
            Pagamento sicuro tramite Stripe · Cancellazione immediata in qualsiasi momento · Crediti rinnovati ogni mese
          </p>
          <p className="mt-2 text-center text-xs text-[#9A968B]">
            Operazione senza applicazione dell'IVA · art. 1, commi 54-89, L. 190/2014 (regime forfettario) · Fattura elettronica via SDI inclusa
          </p>
        </div>

        {/* Fattura section · iter v29 r261 — sostituito mailto con link form */}
        <div className="mt-20 card-flat p-8 bg-white" data-testid="invoice-section">
          <div className="flex items-start gap-4">
            <span className="text-3xl flex-shrink-0">🧾</span>
            <div>
              <h3 className="font-editorial text-2xl">Hai bisogno della fattura?</h3>
              <p className="mt-3 text-[#1C1C1A] leading-relaxed">
                Apri il form <Link className="underline text-[#7C8B6E]" to="/contatti?argomento=fattura">Richiesta fattura</Link> indicando i tuoi dati aziendali (ragione sociale, P.IVA, indirizzo, codice SDI) come nella pagina <Link to="/dashboard/profile" className="underline text-[#7C8B6E]">Profilo</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* iter v28 r350 — Banner "Trasparenza by design"
            Pronto è uno dei pochi assistenti immobiliari che dichiara
            esplicitamente cosa fa e cosa NON fa la sua estensione Chrome.
            Punto di forza competitivo (molti competitor scaricano estensioni
            senza disclaimer né consenso). */}
        <div
          className="mt-12 card-flat p-8 lg:p-10 bg-gradient-to-br from-[#F5F1E8] to-[#FAF6EC] border-[#E5DCC4]"
          data-testid="transparency-banner"
        >
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#7C8B6E] text-white flex-shrink-0">
              <ShieldCheck size={22} weight="fill" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-1">
                Trasparenza by design
              </div>
              <h3 className="font-editorial text-2xl lg:text-3xl text-[#1C1C1A] mb-3 leading-tight">
                L'unico assistente immobiliare che ti dice in chiaro <span className="italic-accent text-[#7C8B6E]">cosa fa</span> e <span className="italic-accent text-[#7C8B6E]">cosa no</span>.
              </h3>
              <p className="text-[14px] text-[#3F3D38] leading-relaxed">
                Pronto Publisher (la nostra estensione Chrome per Immobiliare.it e Idealista) chiede consenso esplicito al primo avvio, ti spiega in italiano semplice cosa automatizza e cosa NO, e <strong>non clicca mai "Pubblica" al tuo posto</strong>. Niente CAPTCHA bypass, niente scraping, niente trucchi. Solo un acceleratore onesto del tuo lavoro.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                <div className="bg-white/70 rounded-lg p-3 border border-[#EAE0C8]">
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-0.5">Consenso esplicito</div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-snug">Checkbox + termini d'uso italiani al primo install.</div>
                </div>
                <div className="bg-white/70 rounded-lg p-3 border border-[#EAE0C8]">
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-0.5">Pilota sei tu</div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-snug">Il click finale "Pubblica" resta sempre tuo, mai automatico.</div>
                </div>
                <div className="bg-white/70 rounded-lg p-3 border border-[#EAE0C8]">
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-0.5">Zero scorciatoie</div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-snug">Non aggiriamo CAPTCHA, anti-bot o controlli dei portali.</div>
                </div>
              </div>
              <Link
                to="/extension/terms"
                className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5B6B4E] hover:text-[#1C1C1A] underline underline-offset-2"
                data-testid="transparency-tos-link"
              >
                Leggi i termini d'uso dell'estensione →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E5E7E0] py-10 mt-12 bg-[#FFFFFF]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col gap-4 text-xs text-[#6B6A65]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/terms?tab=privacy" className="hover:text-[#7C8B6E]" data-testid="footer-privacy">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms?tab=refund" className="hover:text-[#7C8B6E]" data-testid="footer-refund">Refund Policy</Link>
            <span>·</span>
            <Link to="/terms?tab=general" className="hover:text-[#7C8B6E]" data-testid="footer-terms">Termini e Condizioni</Link>
            <span>·</span>
            <Link to="/terms?tab=demo" className="hover:text-[#7C8B6E]" data-testid="footer-demo-terms">Termini Demo Gratuita</Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <ProntoLogo size={16} />
            <span>© {new Date().getFullYear()} Prontoimmo · P.IVA 03868770169 · Bergamo · Italia</span>
          </div>
          <div>
            <Link to="/contatti" className="underline hover:text-[#7C8B6E]" data-testid="pricing-footer-contatti">
              Contattaci
            </Link>
          </div>
        </div>
      </footer>

      {/* iter v29 r275 — Modal di consenso rinuncia recesso 14gg
          (Codice del Consumo art. 59 lett. o). Obbligatorio per servizi
          digitali a esecuzione immediata. Senza checkbox il checkout non parte. */}
      {waiverDialog.open && (
        <div
          className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6 animate-in fade-in duration-150"
          onClick={() => setWaiverDialog({ open: false, plan: null })}
          data-testid="waiver-dialog-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7 relative animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="waiver-dialog"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold mb-2">
              Conferma · Codice del Consumo art. 59
            </div>
            <h3 className="font-editorial text-[24px] text-[#1C1C1A] leading-tight">
              Iniziamo subito
            </h3>

            <p className="mt-3 text-[13.5px] text-[#3F3D38] leading-relaxed">
              Pronto è un servizio digitale: dal momento dell&apos;attivazione hai
              accesso immediato ai crediti del piano <strong>{waiverDialog.plan?.name}</strong>.
              L&apos;addebito mensile parte subito e si rinnova ogni mese; puoi disdire in qualsiasi momento dalla pagina &quot;Gestisci abbonamento&quot; e l&apos;abbonamento si chiude a fine ciclo.
            </p>

            <p className="mt-3 text-[13.5px] text-[#3F3D38] leading-relaxed">
              La legge italiana (Codice del Consumo) ti darebbe normalmente 14 giorni di tempo per cambiare idea e chiedere il rimborso. Quel diritto però vale solo se il servizio digitale <strong>non viene ancora usato</strong>. Visto che Pronto parte subito (e tu vuoi usarlo subito), ti chiediamo di rinunciare a quel diritto di recesso.
            </p>

            <p className="mt-3 text-[13.5px] text-[#3F3D38] leading-relaxed">
              In pratica: <strong>nessun rimborso sul singolo mese già pagato</strong>, ma <strong>puoi disdire il rinnovo quando vuoi</strong> dalla pagina &quot;Gestisci abbonamento&quot;. Tutto resta attivo fino a fine ciclo.
            </p>

            <label className="mt-5 flex gap-3 items-start cursor-pointer bg-[#FAF8F4] border border-[#E5E7E0] rounded-lg p-3.5 hover:border-[#D5DAC9]" data-testid="waiver-checkbox-label">
              <input
                type="checkbox"
                checked={waiverChecked}
                onChange={(e) => setWaiverChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#7C8B6E] shrink-0 cursor-pointer"
                data-testid="waiver-checkbox"
              />
              <span className="text-[12.5px] text-[#1C1C1A] leading-snug">
                Accetto l&apos;erogazione immediata del servizio digitale e <strong>rinuncio espressamente al diritto di recesso</strong> di 14 giorni previsto dal Codice del Consumo. Confermo di aver letto e accettato i <Link to="/terms" className="underline text-[#7C8B6E]" target="_blank" rel="noreferrer">Termini e Condizioni</Link> e l&apos;<Link to="/privacy" className="underline text-[#7C8B6E]" target="_blank" rel="noreferrer">Informativa Privacy</Link>.
              </span>
            </label>

            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
              <button
                onClick={() => setWaiverDialog({ open: false, plan: null })}
                className="flex-1 py-2.5 rounded-md text-[13px] font-medium border border-[#EAE7DF] text-[#6B6A65] hover:bg-[#F1F4ED]"
                data-testid="waiver-cancel"
              >
                Annulla
              </button>
              <button
                onClick={confirmWaiverAndCheckout}
                disabled={!waiverChecked || loading}
                className="flex-1 py-2.5 rounded-md text-[13px] font-semibold bg-[#7C8B6E] text-white hover:bg-[#6E7C61] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="waiver-confirm"
              >
                {loading ? "Apro il checkout…" : "Procedi al pagamento"}
              </button>
            </div>

            <p className="mt-4 text-[10.5px] text-[#9A968B] leading-snug">
              Stiamo registrando data, ora e indirizzo IP del tuo consenso come prova del rispetto della normativa. Puoi sempre disdire dalla sezione &quot;Gestisci abbonamento&quot;: l&apos;abbonamento si chiude a fine ciclo, senza altri addebiti.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
